"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import {
  ArrowUpRight,
  Check,
  Download,
  ExternalLink,
  GitFork,
  Lock,
  Terminal,
} from "@/components/icons";
import {
  buttonVariants,
  FeatureCard,
  HeroGlow,
  PricingCtaSection,
  SectionHeader,
} from "@/components/ui";
import CodeBlock from "@/components/ui/code-block";
import {
  AccentIconChip,
  AccentPill,
  accentTextStyle,
} from "@/components/sections/tools/_shared/parts";
import { cn } from "@/lib/utils";
import { DOCS, GITHUB_MCP_URL, MCP_INSTALL_CMD, NPM_MCP_URL } from "@/lib/constants";
import { MCP_FEATURES, MCP_CATEGORIES } from "./data";
import {
  MCP_CLAUDE_CODE_CODE,
  MCP_CLAUDE_DESKTOP_CODE,
  MCP_OTHER_CLIENTS_CODE,
  MCP_STEPS_COPY,
  MCP_SUPPORTED_CLIENTS,
} from "./content";
import type { McpClientConfig } from "./types";

const easeOut: [number, number, number, number] = [0.22, 1, 0.36, 1];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

interface HeroProps {
  npmDownloads?: number | null;
}

export function McpHero({ npmDownloads }: HeroProps) {
  const prefersReducedMotion = useReducedMotion();

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay, ease: easeOut },
  });

  return (
    /* overflow-y-clip, never x: the HeroGlow ribbon is 100vw wide and has to
       bleed past the shell's frame hairlines like every other hero on the site.
       Clipping x would cut the backdrop back to the 1280px content column. */
    <section className="border-border relative overflow-y-clip border-b pt-28 pb-16 sm:pt-32 sm:pb-20">
      <HeroGlow align="left" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div>
          <motion.div {...fadeUp(0)} className="mb-4">
            <span className="border-border bg-foreground/5 text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium">
              <Terminal size={13} style={accentTextStyle()} aria-hidden="true" />
              MCP Server
            </span>
          </motion.div>
          <motion.p {...fadeUp(0.03)} className="text-muted-foreground mb-3 text-sm font-medium">
            Model Context Protocol
          </motion.p>
          <motion.h1
            {...fadeUp(0.05)}
            className="text-foreground text-4xl leading-[1.08] font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Control your proxies from any AI agent
          </motion.h1>
          <motion.p
            {...fadeUp(0.12)}
            className="text-muted-foreground mt-5 max-w-xl text-lg leading-relaxed"
          >
            The official Proxidize MCP server. Rotate IPs, switch locations, monitor usage, tag
            proxies, and control access — all through natural language, in Claude, Cursor, and any
            other MCP client.
          </motion.p>

          <motion.div {...fadeUp(0.16)} className="mt-8 max-w-lg">
            <CodeBlock code={MCP_INSTALL_CMD} lang="bash" />
          </motion.div>

          <motion.div {...fadeUp(0.2)} className="flex flex-wrap items-center gap-3">
            <span className="cta-glow-btn-wrap">
              <a
                href={GITHUB_MCP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-glow-btn-inner"
              >
                View on GitHub
                <ArrowUpRight size={16} aria-hidden="true" />
              </a>
            </span>
            <a
              href={NPM_MCP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ variant: "outline", size: "lg" }), "font-semibold")}
            >
              View on npm
              <ExternalLink size={14} aria-hidden="true" />
            </a>
          </motion.div>

          <motion.ul
            {...fadeUp(0.26)}
            className="text-muted-foreground mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
          >
            <li className="flex items-center gap-2">
              <Check size={14} style={accentTextStyle()} aria-hidden="true" />
              Free & open source (MIT)
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} style={accentTextStyle()} aria-hidden="true" />
              Works with any MCP client
            </li>
            {typeof npmDownloads === "number" && (
              <li className="flex items-center gap-2">
                <Download size={14} style={accentTextStyle()} aria-hidden="true" />
                {npmDownloads.toLocaleString("en-US")}+ npm installs
              </li>
            )}
          </motion.ul>
        </div>

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: easeOut }}
          className="relative"
        >
          <div
            className="pointer-events-none absolute -inset-8 opacity-60"
            style={{
              background:
                "radial-gradient(ellipse 60% 55% at 50% 50%, color-mix(in srgb, var(--brand-green) 12%, transparent), transparent 70%)",
            }}
            aria-hidden="true"
          />
          {/* Dark-fixed, like every other terminal on the site: a shell reads as
              a screenshot, so its internals are pinned with local token
              overrides instead of flipping with the theme. */}
          <figure
            className={cn(
              "[--background:#0a0a0b] [--border:#27272a] [--brand-green:#5b8cff] [--card:#111113]",
              "[--foreground:#f7f7f8] [--muted-foreground:#a0a0a0] [--surface-2:#161618]",
              "border-border bg-card relative overflow-hidden rounded-xl border"
            )}
            style={{
              boxShadow:
                "0 20px 60px -24px color-mix(in srgb, var(--brand-green) 40%, transparent)",
            }}
          >
            <div className="border-border flex items-center gap-3 border-b bg-[--surface-2] px-4 py-2.5">
              <div className="flex gap-1.5" aria-hidden="true">
                <span className="bg-foreground/10 size-2.5 rounded-full" />
                <span className="bg-foreground/10 size-2.5 rounded-full" />
                <span className="bg-foreground/10 size-2.5 rounded-full" />
              </div>
              <div className="border-border bg-background/60 text-muted-foreground flex min-w-0 flex-1 items-center gap-1.5 rounded-md border px-3 py-1 text-xs">
                <Lock size={10} style={accentTextStyle()} aria-hidden="true" />
                <span className="truncate">proxidize-mcp</span>
              </div>
            </div>
            <div className="space-y-4 p-5 font-mono text-[13px] leading-relaxed">
              <p className="text-muted-foreground">
                $ claude mcp add proxidize -e PROXIDIZE_API_TOKEN=*** -- npx -y @proxidize/mcp
              </p>
              <p>
                <span style={accentTextStyle()}>{"›"}</span>{" "}
                <span className="text-foreground">
                  Rotate my Chicago proxy and give me the new IP
                </span>
              </p>
              <p style={{ color: "#ff8a5b" }}>⏺ rotate_proxy(city: &quot;chicago&quot;)</p>
              <p className="text-muted-foreground pl-4">⎿ old_ip → new_ip (Chicago)</p>
              <p className="text-foreground">Rotated — you&apos;re now on a fresh IP in Chicago.</p>
            </div>
          </figure>
        </motion.div>
      </div>
    </section>
  );
}

export function McpIntro() {
  const prefersReducedMotion = useReducedMotion();
  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
          className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16"
        >
          <div className="lg:self-start">
            <SectionHeader
              eyebrow="Overview"
              heading="Your proxy infrastructure, one prompt away"
            />
          </div>
          <div className="space-y-5">
            <p
              className="text-foreground border-l-2 pl-6 text-xl leading-relaxed"
              style={{ borderColor: "color-mix(in srgb, var(--brand-green) 45%, transparent)" }}
            >
              The Proxidize MCP server gives Claude, Cursor, and other MCP clients direct access to
              your proxy infrastructure — no dashboard tab-switching required.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              On startup it checks which Proxidize subscriptions are active on your account and
              registers only the relevant tools. A Per Proxy plan gets proxy management tools, Per
              GB gets usage and access point tools, and so on — nothing is exposed that your account
              can&apos;t use.
            </p>
            <div
              className="bg-card relative my-2 overflow-hidden rounded-2xl border px-7 py-6"
              style={{
                borderColor: "color-mix(in srgb, var(--brand-green) 22%, transparent)",
                backgroundImage:
                  "radial-gradient(120% 140% at 0% 0%, color-mix(in srgb, var(--brand-green) 13%, transparent), transparent 64%)",
              }}
            >
              <p style={accentTextStyle()} className="text-sm font-semibold">
                Smart tool discovery
              </p>
              <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                14 tools for Per Proxy subscribers, 9 for Per GB mobile, 9 for Per GB residential, 3
                analytics tools for any active plan, plus 2 account tools that always load — 37
                tools total, scoped automatically to what you actually pay for.
              </p>
            </div>
            <p className="text-muted-foreground text-sm">
              New to MCP?{" "}
              <Link
                href="/blog/what-is-mcp-and-how-does-model-context-protocol-work/"
                className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
              >
                Read what MCP is and how Model Context Protocol works
              </Link>
              .
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function McpFeatureGrid() {
  const prefersReducedMotion = useReducedMotion();
  return (
    <section className="py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Features"
            heading="Built for AI agents, not another dashboard"
            body="A thin, well-behaved MCP server on top of the Proxidize API — nothing to babysit."
          />
        </div>
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
        >
          {/* wash keeps the grid off flat white on the light theme. "blue" is
              the single-hue variant: on a grid this size the warm corner of the
              default wash reads as a second kind of card. Rotates corner every
              third tile. */}
          {MCP_FEATURES.map((feature) => (
            <FeatureCard
              key={feature.title}
              title={feature.title}
              description={feature.description}
              wash="blue"
              className="card-hover card-spotlight h-full"
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export function McpToolCategories() {
  const prefersReducedMotion = useReducedMotion();
  return (
    <section className="full-bleed band-wash bg-band py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Available tools"
            heading="37 tools, grouped by what you're subscribed to"
            body="Every tool the server can register, organized the same way the server discovers them at startup."
          />
        </div>
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
        >
          {MCP_CATEGORIES.map((category) => {
            const Icon = category.icon;
            const preview = category.tools.slice(0, 5);
            const remaining = category.tools.length - preview.length;
            return (
              <div
                key={category.title}
                className="border-border bg-card card-wash-blue card-hover relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl border p-6"
              >
                <div className="relative flex items-center gap-3">
                  <AccentIconChip icon={Icon} size="sm" />
                  <div>
                    <h3 className="text-foreground text-base font-semibold">{category.title}</h3>
                    <p className="text-muted-foreground text-xs">{category.subtitle}</p>
                  </div>
                </div>
                <div className="relative flex flex-wrap gap-1.5">
                  {preview.map((tool) => (
                    <code
                      key={tool.name}
                      title={tool.description}
                      className="text-muted-foreground border-foreground/8 bg-foreground/4 rounded-md border px-2 py-1 font-mono text-[11px]"
                    >
                      {tool.name}
                    </code>
                  ))}
                  {remaining > 0 && (
                    <AccentPill label={`+${remaining} more`} className="rounded-md py-1" />
                  )}
                </div>
              </div>
            );
          })}
        </motion.div>
        <p className="text-muted-foreground mt-8 text-sm leading-relaxed">
          Full tool reference and parameters are documented on{" "}
          <a
            href={GITHUB_MCP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            GitHub
          </a>
          .
        </p>
      </div>
    </section>
  );
}

const CLIENT_TABS: McpClientConfig[] = [
  { slug: "claude-code", label: "Claude Code", code: MCP_CLAUDE_CODE_CODE, lang: "bash" },
  { slug: "claude-desktop", label: "Claude Desktop", code: MCP_CLAUDE_DESKTOP_CODE, lang: "json" },
  {
    slug: "other",
    label: "Cursor / Windsurf / VS Code",
    code: MCP_OTHER_CLIENTS_CODE,
    lang: "json",
  },
];

export function McpQuickStart() {
  const prefersReducedMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const client = CLIENT_TABS[active];

  return (
    <section className="py-section border-border border-b">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Quick start"
            heading="Up and running in under a minute"
            body="Grab your API token, add the server to your client, and start asking."
          />
        </div>

        <motion.ol
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
          className="relative mb-12 grid gap-10 sm:grid-cols-3 sm:gap-8"
        >
          <div
            className="via-border absolute top-5 right-12 left-12 hidden h-px bg-linear-to-r from-transparent to-transparent sm:block"
            aria-hidden="true"
          />
          {MCP_STEPS_COPY.map((step, index) => (
            <li key={step.title} className="relative">
              <span
                className="relative z-10 mb-5 flex size-10 items-center justify-center rounded-full border text-sm font-bold"
                style={{
                  // Mixed into the page fill rather than laid over it: the chip
                  // has to stay opaque to mask the connector line behind it.
                  backgroundColor: "color-mix(in srgb, var(--brand-green) 12%, var(--background))",
                  borderColor: "color-mix(in srgb, var(--brand-green) 30%, transparent)",
                  color: "var(--brand-green)",
                }}
              >
                {index + 1}
              </span>
              <h3 className="text-foreground text-lg font-semibold">{step.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                {step.description}
              </p>
            </li>
          ))}
        </motion.ol>

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5, ease: easeOut }}
        >
          <div
            role="tablist"
            aria-label="MCP client"
            className="border-border mb-4 flex flex-wrap gap-2 border-b pb-4"
          >
            {CLIENT_TABS.map((tab, index) => {
              const isActive = index === active;
              return (
                <button
                  key={tab.slug}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(index)}
                  className={cn(
                    "rounded-lg border px-3.5 py-2 text-sm transition-colors",
                    isActive
                      ? "font-semibold"
                      : "text-muted-foreground hover:text-foreground border-transparent font-medium"
                  )}
                  style={
                    isActive
                      ? {
                          borderColor: "color-mix(in srgb, var(--brand-green) 30%, transparent)",
                          backgroundColor:
                            "color-mix(in srgb, var(--brand-green) 10%, transparent)",
                          color: "var(--brand-green)",
                        }
                      : undefined
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <CodeBlock code={client.code} lang={client.lang} />
        </motion.div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="text-muted-foreground text-sm">Supported clients:</span>
          {MCP_SUPPORTED_CLIENTS.map((name) => (
            <span
              key={name}
              className="border-border text-muted-foreground rounded-full border px-3 py-1 text-xs font-medium"
            >
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* The site-wide closing band. The actions are this page's own — a repo and a
   package, not a trial — so they come in through `actions`; everything else is
   the shared CTA, so this closes the page the same way every other page does. */
export function McpCta() {
  return (
    <PricingCtaSection
      id="mcp-cta-heading"
      heading="Add Proxidize to your AI agent"
      body={
        <>
          Free, open source, and ready in one command. Pair it with{" "}
          <Link
            href="/proxy-server/mobile-proxies"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            Proxidize mobile
          </Link>{" "}
          or{" "}
          <Link
            href="/proxy-server/residential-proxies"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            residential proxies
          </Link>
          .
        </>
      }
      perks={[
        { icon: Check, label: "MIT licensed" },
        { icon: Check, label: "No build step" },
        { icon: GitFork, label: "Open to contributions" },
      ]}
      actions={
        <>
          <span className="cta-glow-btn-wrap">
            <a
              href={GITHUB_MCP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-glow-btn-inner"
            >
              View on GitHub
              <ArrowUpRight size={16} aria-hidden="true" />
            </a>
          </span>
          <a
            href={NPM_MCP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: "outline", size: "lg" }), "font-semibold")}
          >
            <Download size={14} aria-hidden="true" />
            View on npm
          </a>
        </>
      }
      footnote={
        <>
          Need help getting set up? Browse the{" "}
          <a
            href={DOCS.mcpServer}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
          >
            MCP server documentation
          </a>
          .
        </>
      }
    />
  );
}
